<script lang="ts">
  import { page, router } from '@inertiajs/svelte'
  import { onMount } from 'svelte'

  const FLASH_TYPES = [
    'notice',
    'alert',
    'warning',
    'info',
    'success',
    'error',
  ] as const
  type FlashType = (typeof FLASH_TYPES)[number]
  const TOAST_WHITELIST = new Set<string>(FLASH_TYPES)

  interface FlashMessage {
    id: string
    type: string
    message: string
    action?: {
      label: string
      path: string
    }
  }

  let visibleMessages = $state<FlashMessage[]>([])
  let timers: ReturnType<typeof setTimeout>[] = []
  let messageCounter = 0

  const flashStyles: Record<FlashType, string> = {
    notice: 'bg-success-100 text-success-800',
    alert: 'bg-danger-100 text-danger-800',
    warning: 'bg-warning-100 text-warning-800',
    info: 'bg-primary-100 text-primary-800',
    success: 'bg-success-100 text-success-800',
    error: 'bg-danger-100 text-danger-800',
  }

  function getFlashClasses(type: string): string {
    return flashStyles[type as FlashType] || 'bg-neutral-100 text-neutral-800'
  }

  function processFlash(flash: Record<string, unknown> | null | undefined) {
    if (!flash) return

    timers.forEach((timer) => clearTimeout(timer))
    timers = []

    const messages: FlashMessage[] = Object.entries(flash)
      .filter(
        ([key, value]) => typeof value === 'string' && TOAST_WHITELIST.has(key)
      )
      .map(([key, value]) => ({
        id: `${key}-${Date.now()}-${messageCounter++}`,
        type: key,
        message: String(value),
      }))

    const undo = flash.discovery_undo
    if (
      typeof undo === 'object' &&
      undo !== null &&
      'message' in undo &&
      typeof undo.message === 'string' &&
      'path' in undo &&
      typeof undo.path === 'string'
    ) {
      messages.push({
        id: `discovery-undo-${Date.now()}-${messageCounter++}`,
        type: 'notice',
        message: undo.message,
        action: { label: 'Undo', path: undo.path },
      })
    }

    if (messages.length > 0) {
      visibleMessages = messages

      timers = messages.map((msg) => {
        // Errors stay long enough to actually read; successes can be brief
        const duration = msg.action
          ? 7000
          : ['alert', 'error', 'warning'].includes(msg.type)
            ? 6000
            : 3000
        return setTimeout(() => {
          removeMessage(msg.id)
        }, duration)
      })
    }
  }

  onMount(() => {
    processFlash(page.props.flash)

    const removeListener = router.on('success', () => {
      processFlash(page.props.flash)
    })

    return () => {
      timers.forEach((timer) => clearTimeout(timer))
      removeListener()
    }
  })

  function removeMessage(id: string) {
    visibleMessages = visibleMessages.filter((msg) => msg.id !== id)
  }

  function runAction(message: FlashMessage) {
    if (!message.action) return

    router.delete(message.action.path, {
      preserveScroll: true,
      onSuccess: () => removeMessage(message.id),
    })
  }
</script>

<div
  class="fixed top-12 left-1/2 -translate-x-1/2 z-50 w-full flex justify-center pt-4 pointer-events-none"
  role="status"
  aria-live="polite"
>
  {#each visibleMessages as message (message.id)}
    <div
      class="pointer-events-auto mx-4 flex w-full max-w-2xl items-center gap-4 rounded-lg px-4 py-3 shadow-lg sm:w-auto sm:px-6 sm:py-4 {getFlashClasses(
        message.type
      )}"
    >
      <p class="font-medium flex-1">{message.message}</p>
      {#if message.action}
        <button
          type="button"
          onclick={() => runAction(message)}
          class="shrink-0 rounded-md px-3 py-1.5 text-sm font-semibold transition hover:opacity-70"
        >
          {message.action.label}
        </button>
      {/if}
      <button
        type="button"
        onclick={() => removeMessage(message.id)}
        aria-label="Close message"
        class="shrink-0 ml-2 hover:opacity-70"
      >
        <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fill-rule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clip-rule="evenodd"
          />
        </svg>
      </button>
    </div>
  {/each}
</div>
